import React from 'react';
import { NavLink, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AnalyticsManager from '../../../../app/analytics';
import { combineClassNames, generateUniqueId, getMediaPath } from '../../../../app/functions';
import SliderHeader from '../../../new_home/components/slider-header';
import ArticleTile from '../ArticleTile';
import VideoArticleTile from '../VideoArticleTile';
import './index.scss';

const ArticlesStrip = ({
    uniqueId,
    title,
    icon,
    linkText,
    linkRoute,
    articles,
    maxTiles,
    analyticsSource = undefined,
    isHomepage = false,
    displayTitle = true,
}) => {
    const history = useHistory();
    const translations = useSelector((store) => store.config.Translations);

    const getTiles = () => {
        if (!maxTiles) {
            return getTilesComponents(articles);
        }

        let tiles = [];
        const min = Math.min(maxTiles, articles.length);

        for (let i = 0; i < min; i++) {
            tiles.push(articles[i]);
        }

        tiles = getTilesComponents(tiles);

        return tiles;
    };

    const getTilesComponents = (tiles) => {
        return tiles.map((articleData, articleIndex) => {
            // Add the 'scroll-to-here' class to the first article/video-article only, for the homepage VerticalStrip component
            const additionalClassName = (isHomepage && articleIndex === 0) ? 'scroll-to-here' : null;

            // Video articles
            if (articleData.type.toLowerCase().startsWith('video')) {
                return (
                    <VideoArticleTile
                        key={`${uniqueId}-${articleData.type}-${articleData.id}`}
                        article={articleData}
                        className={additionalClassName}
                        analyticsSource={analyticsSource}
                    />
                );
            }

            // Normal articles
            return (
                <ArticleTile
                    key={`${uniqueId}-${articleData.type}-${articleData.id}`}
                    article={articleData}
                    className={additionalClassName}
                    analyticsSource={analyticsSource}
                />
            );
        })
    };

    const TitleContainer = ({ children }) => React.createElement(
        linkRoute ? NavLink : 'div',
        { to: linkRoute || null, className: 'article-strip-title' },
        children
    );

    const handleHomepageSliderClick = () => {
        AnalyticsManager.logMagazineSliderPress();
        history.push(linkRoute);
    }

    return (
        <div
            id={uniqueId}
            className={combineClassNames(
                'articles-strip-wrapper',
                isHomepage && 'articles-strip-wrapper_homepage',
            )}
        >
            {displayTitle && (
              title ? (
                isHomepage ? (
                  <SliderHeader
                    id={`${uniqueId}_header`}
                    title={title}
                    iconPath={getMediaPath(icon)}
                    onTitleClick={handleHomepageSliderClick}
                    showMoreButton={linkText}
                    onShowMoreButtonClick={handleHomepageSliderClick}
                    tabsList={[]}
                    buttonsList={[]}
                    className="magazine-homepage-slider-header"
                  />
                ) : (
                  <div className="article-strip-title-link">
                      <TitleContainer>
                          {icon && (
                            <img
                              src={getMediaPath(icon)}
                              alt={title}
                              className="article-strip-icon"
                            />
                          )}

                          <div className="article-strip-title-content">
                              {title}
                          </div>
                      </TitleContainer>

                      {linkRoute && (
                        <NavLink to={linkRoute} className="article-strip-link">
                            {linkText}
                        </NavLink>
                      )}
                  </div>
                )
              ) : (
                <div className="article-strip-title-link skeleton">
                    <div className="shimmer-wrapper">
                        <div className="shimmer" />
                    </div>
                </div>
              )
            )}
            <div
                className={combineClassNames(
                    'articles-strip',
                    isHomepage && 'articles-strip_homepage',
                )}
            >
                {!articles && Array.from({ length: maxTiles || 9 }).map(
                    () => (<VideoArticleTile key={generateUniqueId(8)} skeleton />)
                )}

                {articles?.length > 0 && getTiles()}

                {articles?.length === 0 && (
                    <div className="no-result">
                        {translations['magazine_no_results']}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ArticlesStrip;
